import React, {useEffect, useMemo} from 'react';
import {withRouter} from 'react-router';
import {fromJS, Map} from 'immutable';
import {stats} from 'lib/StatsUtil';
import {callAction} from 'client/framework';
import SelectableDateRange from 'generic/SelectableDateRange/SelectableDateRange.react';
import ViewWithSidebar from 'generic/ViewWithSidebar/ViewWithSidebar.react';
import {dateRangeOptions} from 'generic/SelectableDateRange/shared';
import LoadingIndicator from 'client/generic/LoadingIndicator.react';
import appStore from 'client/AppStore';
import Tabs from 'sg/Tabs/Tabs.react';
import {makeLinkRowProps} from 'sg/Tables/Table/table.utils';
import {MandarkTable, Column} from 'sg/Tables/MandarkTable/MandarkTable.react';
import DueDateContent from 'client/Classrooms/shared/DueDateContent/DueDateContent.react';

import studentAssignmentsStore from './StudentAssignments.store';
import studentAssignmentsActions from './StudentAssignments.actions';
import NoAssignmentsFoundPlaceholder from './NoAssignmentsFoundPlaceholder.react';

import './assignments.scss';

const Assignments = ({params}: {params: {classId: string}}) => {
  const {classId} = params;
  const storeClassId = studentAssignmentsStore.getClassroomId();
  const studentId = studentAssignmentsStore.getStudentId();

  useEffect(() => {
    callAction(studentAssignmentsActions.SET_CLASSROOM_ID, classId);
  }, [classId]);

  const handleChange = (option: Map<string, string | number>) => {
    callAction(studentAssignmentsActions.SET_DUE_DATE_RANGE_ID, option.get('id'));
  };

  return (
    <ViewWithSidebar
      sidebarItems={[
        {
          label: 'Filters',
          content: (
            <SelectableDateRange
              className='student-assignments__filter card'
              defaultDateRange={studentAssignmentsStore.getDueDateRangeId()}
              endDateLabel='End'
              label='Due Date'
              onChange={handleChange}
              options={dateRangeOptions}
              onInvalidChange={handleChange}
              shouldShowErrors
              startDateLabel='Start'
              storeName={studentAssignmentsStore.getSelectableDateRangeStoreName()}
            />
          )
        }
      ]}
      mainItems={[
        {
          label: 'My Assignments',
          content: storeClassId ? (
            <AssignmentsTable classroomId={storeClassId} studentId={studentId} />
          ) : (
            <LoadingIndicator />
          )
        }
      ]}
    />
  );
};

interface AssignmentsTableProps {
  classroomId: string;
  studentId: string;
}

const AssignmentsTable = ({classroomId, studentId}: AssignmentsTableProps) => {
  const getAssignmentLinkProps = () => {
    return makeLinkRowProps((assignment) => `/assignment/${assignment.getId()}`);
  };

  const getAssignmentQueryWithParamStatus = () => {
    const assignmentStatusParam = appStore.routerProps.location.query.assignmentStatus;
    return studentAssignmentsStore.getAssignmentsQuery(assignmentStatusParam);
  };

  const getAssignmentQueryResourceCount = (status: string | null) => {
    const query = studentAssignmentsStore.getAssignmentsQuery(status).pageSize(1);
    query.getResource();
    return query.getResourceMetadata().getIn(['page', 'total_entries']);
  };

  const tabs = useMemo(
    () =>
      fromJS([
        {
          label: 'All',
          query: {
            assignmentStatus: 'all'
          },
          badgeContent: () => getAssignmentQueryResourceCount('all')
        },
        {
          label: 'Open',
          query: {
            assignmentStatus: null
          },
          badgeContent: () => getAssignmentQueryResourceCount(null)
        },
        {
          label: 'Closed',
          query: {
            assignmentStatus: 'closed'
          },
          badgeContent: () => getAssignmentQueryResourceCount('closed')
        }
      ]),
    []
  );

  return (
    <Tabs content={tabs}>
      <MandarkTable
        mobileBreakpoint={700}
        query={getAssignmentQueryWithParamStatus()}
        storeName='StudentAssignmentsTable'
        rowPropsFunc={getAssignmentLinkProps()}
        emptyTableElement={<NoAssignmentsFoundPlaceholder />}
      >
        <Column
          content={(assignment) => (
            <DueDateContent
              assignment={assignment}
              classroomId={classroomId}
              studentId={studentId}
            />
          )}
          defaultSortParam
          heading='Due date'
          sortBy='due_date,name'
        />
        <Column
          content={(assignment) => <div>{assignment.getName()}</div>}
          heading='Assignment'
          sortBy='name'
        />
        <Column
          content={(assignment) => assignment.getMeta().getCountOfQuestions()}
          heading='Questions'
          sortBy='meta.count_of_questions,name'
        />
        <Column
          heading='Grade'
          content={(assignment) => {
            let grade;
            if (assignment.getMeta().isStudentSubmitted() && assignment.shouldShowGuessFeedback()) {
              grade = `${stats.percentage(
                assignment.getMeta().getStudentCountOfCorrectGuesses(),
                assignment.getMeta().getCountOfQuestions()
              )}%`;
            } else {
              grade = 'n/a';
            }
            return grade;
          }}
        />
      </MandarkTable>
    </Tabs>
  );
};

export default withRouter(Assignments);
